import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Link, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { supabase } from '../../services/supabase';
import { Material } from '../../types/models';
import LaunchIcon from '@mui/icons-material/Launch';

const MaterialsList: React.FC = () => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('materials')
        .select('*')
        .order('category')
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      setMaterials(data || []);
    } catch (err: any) {
      console.error('Error fetching materials:', err);
      setError(err.message || 'An error occurred while fetching materials');
    } finally {
      setLoading(false);
    }
  };

  // Group materials by category
  const groupedMaterials = materials.reduce((acc, material) => {
    const category = material.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(material);
    return acc;
  }, {} as Record<string, Material[]>);

  // Sort categories alphabetically
  const sortedCategories = Object.keys(groupedMaterials).sort();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (materials.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
        No study materials available yet.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Study Materials
      </Typography>
      
      {sortedCategories.map((category) => (
        <Card key={category} sx={{ mb: 3, overflow: 'visible' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary" sx={{ 
              borderBottom: '1px solid', 
              borderColor: 'divider',
              pb: 1
            }}>
              {category}
            </Typography>
            
            <List disablePadding>
              {groupedMaterials[category].map((material, index) => (
                <React.Fragment key={material.id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    component={Link}
                    href={material.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      py: 1.5,
                      color: 'inherit',
                      textDecoration: 'none',
                      '&:hover': {
                        bgcolor: 'rgba(0, 0, 0, 0.04)',
                      }
                    }}
                  >
                    <ListItemText 
                      primary={material.name} 
                      primaryTypographyProps={{ 
                        color: 'text.primary',
                        sx: {
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                    />
                    <LaunchIcon fontSize="small" sx={{ ml: 1, opacity: 0.6 }} />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default MaterialsList;